// Description: Page to see and update the info of the campaign
// Updated: 29/09/2025
// Author: Simon Franklin

// React
import React, { useEffect, useState } from "react";

// Material UI Components
import { Box, Step, StepLabel, Stepper } from "@mui/material";

// Components and functions for the project
import HeaderPage from "../../components/HeaderPage/HeaderPage";
import { CampaignsByUuidAccount } from "../../api/aws/Campaigns/CampaignsByUuidAccount";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { Channels } from "../../api/aws/Channels/Channels";
import { GetListagens } from "../../api/aws/Listagens/GetListagens";
import { GetAllTemplates } from "../../api/aws/Templates/GetAllTemplates";
import { CampaignUpdate } from "../../api/aws/Campaigns/CampaignUpdate";
import { notify, updateMessageType, truncateText } from "../../utils/utils";
import { useNavigate } from "react-router-dom";
import use123zapStore from "../../context/123zapStore";
import themeColors from "../../components/ThemeColors/ThemeColors";
import {
  Step1,
  Step2,
  Step3,
  Step4,
  Step5,
  campaignSteps,
} from "./CampaignCreationForms/campaignCreating";
import LoadingStep from "./CampaignCreationForms/LoadingStep";
import CampaignCreationFooter from "./CampaignCreationForms/CampaignCreationFooter";

dayjs.extend(utc);

// Render the Page
const CampaignInfo = () => {
  const id = localStorage.getItem("campaignId") || null;

  const [loading, setLoading] = useState(false);
  const [listOptions, setListOptions] = useState(null);
  const [columnOptions, setColumnOptions] = useState(null);
  const [wppOptions, setWppOptions] = useState(null);
  const [smsOptions, setSmsOptions] = useState(null);
  const [emailOptions, setEmailOptions] = useState(null);
  const [templateOptions, setTemplateOptions] = useState(null);
  const userInfo = use123zapStore((state) => state.userInfo);
  const setAlreadyFetchCampaigns = use123zapStore((state) => state.setAlreadyFetchCampaigns);
  const initializeFormState = () => {
    const savedState = JSON.parse(sessionStorage.getItem("formState"));
    if (savedState) {
      return Object.keys(savedState).reduce((acc, fieldName) => {
        const value = savedState[fieldName];

        const dateFields = [
          "Data de início",
          "Data de término",
          "Hora de início"
        ];

        if (dateFields.includes(fieldName)) {
          const dateValue = new Date(value);
          acc[fieldName] = isNaN(dateValue.getTime()) ? value : dateValue;
        } else {
          acc[fieldName] = value;
        }

        return acc;
      }, {});
    }

    return {
      "Título da Campanha": "",
      "Descrição da Campanha": "",
      "Categoria": "",
      "Tipo de campanha": "",
      "Envio imediato": "",
      "Data de início": "",
      "Hora de início": "",
      "Data de término": "",
      "Selecionar Listagem": "",
      "Enviar em": "",
      "Em qual coluna estão as datas de vencimento?": "",
      "Dias": "",
      "Referencia de Envio": "",
      "Coluna da Regra": "",
      "Operador": "",
      "Valor": "",
      "Enviar SMS": false,
      "Enviar WhatsApp": false,
      "Enviar Email": false,
      "Canal SMS": "",
      "Canal WhatsApp": "",
      "Canal Email": "",
      "Modelo SMS": "",
      "Modelo WhatsApp": "",
      "Modelo Email": ""
    }
  };
  const [formState, setFormState] = useState(initializeFormState());
  const [currentStep, setCurrentStep] = useState(JSON.parse(sessionStorage.getItem("currentStep")) || 0);
  const [errorState, setErrorState] = useState({
    "Título da Campanha": false,
    "Descrição da Campanha": false,
    "Categoria": false,
    "Tipo de campanha": false,
    "Envio imediato": false,
    "Data de início": false,
    "Hora de início": false,
    "Data de término": false,
    "Selecionar Listagem": false,
    "Enviar em": false,
    "Em qual coluna estão as datas de vencimento?": false,
    "Dias": false,
    "Referencia de Envio": false,
    "Coluna da Regra": false,
    "Operador": false,
    "Valor": false,
    "Enviar SMS": false,
    "Enviar WhatsApp": false,
    "Enviar Email": false,
    "Canal SMS": false,
    "Canal WhatsApp": false,
    "Canal Email": false,
    "Modelo SMS": false,
    "Modelo WhatsApp": false,
    "Modelo Email": false
  });
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([fetchLists(), fetchTemplates(), fetchChannels(), fetchCampaign()]);
      setLoading(false);
    })();
  }, [id]);

  const updateFieldValue = (title, value) => {
    setFormState((prev) => ({
      ...prev,
      [title]: value
    }))
  }

  const canAdvance = () => {
    let nextStep = true;

    if (currentStep === 0) {

      const requiredFields = ["Título da Campanha", "Descrição da Campanha", "Categoria"];

      requiredFields.forEach((field) => {
        if (formState[field] === "" || formState[field] === null) {
          nextStep = false;
          setErrorState((prev) => ({
            ...prev,
            [field]: true
          }))
        }
      });

    } else if (currentStep === 1) {

      let requiredFields = ["Tipo de campanha", "Início da campanha", "Data de início", "Hora de início", "Data de término"];

      if (formState["Tipo de campanha"] === "Recorrência") {
        requiredFields = ["Tipo de campanha", "Início da campanha", "Data de início", "Hora de início", "Data de término"];
      }

      if (formState["Tipo de campanha"] === "Disparo único") {
        requiredFields = ["Tipo de campanha", "Data de início", "Hora de início"];
      }

      if (formState["Tipo de campanha"] === "Disparo único" && formState["Envio imediato"] === true) {
        requiredFields = ["Tipo de campanha"];
      }

      requiredFields.forEach((field) => {
        if (formState[field] === "" || formState[field] === null) {
          nextStep = false;
          setErrorState((prev) => ({
            ...prev,
            [field]: true
          }))
        }
      });

    } else if (currentStep === 2) {
      let requiredFields = ["Selecionar Listagem"];

      requiredFields.forEach((field) => {
        if (formState[field] === "" || formState[field] === null) {
          nextStep = false;
          setErrorState((prev) => ({
            ...prev,
            [field]: true
          }))
        }
      });
    } else if (currentStep === 3) {
      if (formState["Tipo de campanha"] === "Disparo único") {
        if (!formState["Enviar em"]) {
          nextStep = false;
          setErrorState((prev) => ({
            ...prev,
            "Enviar em": true,
          }));
        }
      } else if (formState["Tipo de campanha"] === "Recorrência") {
        const bloco1 = ["Enviar em", "Em qual coluna estão as datas de vencimento?", "Dias", "Referencia de Envio"];
        const bloco2 = ["Enviar em", "Coluna da Regra", "Operador", "Valor"];

        const bloco1Valido = bloco1.every((field) => formState[field] !== "" && formState[field] !== null);
        const bloco2Valido = bloco2.every((field) => formState[field] !== "" && formState[field] !== null);

        if (!bloco1Valido && !bloco2Valido) {
          nextStep = false;

          [...bloco1, ...bloco2].forEach((field) => {
            if (!formState[field]) {
              setErrorState((prev) => ({
                ...prev,
                [field]: true,
              }));
            }
          });
        }
      }
    }

    else if (currentStep === 4) {
      const types = ["SMS", "WhatsApp", "Email"];

      const someSelected = types.some((tipo) => formState[`Enviar ${tipo}`] === true);

      if (!someSelected) {
        nextStep = false;

        notify("Error", "Você precisa enviar por algum canal", true);

        types.forEach((tipo) => {
          setErrorState((prev) => ({
            ...prev,
            [`Enviar ${tipo}`]: true,
          }));
        });
      } else {

        types.forEach((tipo) => {
          if (formState[`Enviar ${tipo}`] === true) {
            if (!formState[`Canal ${tipo}`] || !formState[`Modelo ${tipo}`]) {
              nextStep = false;
              notify("Error", "Defina um canal e um modelo", true);
              setErrorState((prev) => ({
                ...prev,
                [`Canal ${tipo}`]: !formState[`Canal ${tipo}`],
                [`Modelo ${tipo}`]: !formState[`Modelo ${tipo}`],
              }));
            }
          }
        });
      }
    }

    sessionStorage.setItem("formState", JSON.stringify(formState));
    sessionStorage.setItem("currentStep", JSON.stringify(currentStep));
    return nextStep;
  }

  const renderCurrentStep = (step, loading) => {
    if (loading === true) {
      return <LoadingStep />
    } else {
      switch (step) {
        case 0:
          return <Step1 edit={true} formState={formState} error={errorState} setError={setErrorState} updateFieldValue={updateFieldValue} />;
        case 1:
          return <Step2 formState={formState} error={errorState} setError={setErrorState} updateFieldValue={updateFieldValue} />;
        case 2:
          return <Step3 formState={formState} error={errorState} setError={setErrorState} updateFieldValue={updateFieldValue} lists={listOptions} />;
        case 3:
          return <Step4 formState={formState} error={errorState} setError={setErrorState} updateFieldValue={updateFieldValue} columnOptions={columnOptions} />;
        case 4:
          return <Step5 formState={formState} error={errorState} setError={setErrorState} updateFieldValue={updateFieldValue} emailOptions={emailOptions} smsOptions={smsOptions} wppOptions={wppOptions} templateOptions={templateOptions} />;
      }
    }
  };

  React.useEffect(() => {
    if (currentStep === 1 && formState["Tipo de campanha"] === "Disparo único") {
      setFormState((prev) => ({
        ...prev,
        "Data de término": dayjs().startOf("day")
      }))
    }

    if (currentStep === 1 && formState["Tipo de campanha"] === "Recorrência") {
      setFormState((prev) => ({
        ...prev,
        "Início da campanha": false,
        "Envio imediato": false,
      }))
    }

    if (currentStep === 3 && formState["Referencia de Envio"] === "sameDay") {
      setFormState((prev) => ({
        ...prev,
        "Dias": "0"
      }))
    }

    ["SMS", "WhatsApp", "Email"].forEach((tipo) => {
      if (
        formState[`Enviar ${tipo}`] === false &&
        (formState[`Canal ${tipo}`] !== "" || formState[`Modelo ${tipo}`] !== "")
      ) {
        setFormState((prev) => ({
          ...prev,
          [`Canal ${tipo}`]: "",
          [`Modelo ${tipo}`]: "",
        }));
      }
    });

    if (formState["Envio imediato"] === true) {
      setFormState((prev) => ({
        ...prev,
        "Hora de início": "",
        "Data de início": dayjs().startOf("day")
      }))
    }

  }, [formState]);

  React.useEffect(() => {
    if (formState["Selecionar Listagem"] !== null) {
      getColumns();
    }

  }, [currentStep]);

  const fetchLists = async () => {
    setLoading(true);

    try {

      const resultJson = await GetListagens();

      let listagens = resultJson['data'].map((item) => {
        return {
          id: item['code'],
          title: item['title'],
          desc: truncateText(item['description'], 50),
          link: item['url'],
          type: item['type'],
          icon: item['type']
        }
      });

      setListOptions(listagens);

    } catch (error) {
      console.error("Erro ao obter dados da API:", error);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  }

  const getColumns = async () => {
    try {
      const token = localStorage.getItem("jwt_token");
      const clientId = localStorage.getItem("idToken");
      if (!token || !clientId) throw new Error("Token ou ID do cliente não encontrado");

      const response = await fetch(`${process.env.REACT_APP_BACKEND_AWS_URL}/sheets/variables/check?code=${(formState["Selecionar Listagem"])}`, {
        headers: { Authorization: token, Client_ID: clientId, "Content-Type": "application/json" }
      });

      const data = await response.json();

      setColumnOptions(data.data.map((i) => ({ id: i.id, title: i.name, column: i.column })));

    } catch (err) {
      console.error("Erro ao obter colunas:", err);
    }
  };

  const fetchChannels = async () => {
    try {
      const { channels } = await Channels();

      const mapChannel = (filterFn, setState) => {
        let opts = channels.filter(filterFn).map((c) => ({ id: c.code, title: c.channel_identifier }));
        opts.unshift({ id: "redirect", title: "Adicionar Canal", path: "/canais/adicionar" });

        setState(opts)
      };

      mapChannel((c) => String(c.integration).includes("WhatsApp"), setWppOptions);
      mapChannel((c) => c.integration === "SMS", setSmsOptions);
      mapChannel((c) => c.integration === "E-mail", setEmailOptions);
    } catch (err) {
      console.error("Erro ao obter canais:", err);
    }
  };

  const fetchTemplates = async () => {

    try {
      const { data } = await GetAllTemplates();
      const items = data
        .map((item) => ({
          ...item,
          messageJson: String(item.channel_type).includes("WhatsApp") || item.channel_type === "SMS" ? updateMessageType(item.message_json) : item.message_json
        }));

      setTemplateOptions(items);
    } catch (err) {
      console.error("Erro ao obter templates:", err);
    }
  };

  const fetchCampaign = async () => {
    CampaignsByUuidAccount()
      .then((response) => {
        const data = response.campaigns.map((item) => {
          return {
            id: item.code,
            title: item.title,
            start_date: item.start_date,
            expiration_date: item.expiration_date,
            status:
              item.status === "true" || item.status === "active"
                ? "Active"
                : "Desativado",
            icon: "Campanha",
            desc: item.description,
            visibility: item.visibility === true ? "Visível" : "Oculto",
            type: item.type,
            start_time: item.start_time,
            ...item,
          };
        });

        const findCampaign = data.find((item) => item.id === id);

        const [hours, minutes, seconds] = findCampaign.start_time.split(":").map(Number);

        const startTime = new Date();
        startTime.setHours(hours, minutes, seconds || 0, 0);

        setFormState(prev => ({
          ...prev,
          "Título da Campanha": findCampaign.title ?? null,
          "Status da Campanha": findCampaign.status ?? null,
          "Categoria": findCampaign.type ?? null,
          "Descrição da Campanha": findCampaign.description ?? null,
          "Tipo de campanha": findCampaign.start_immediately === false ? "Recorrência" : "Disparo único",
          "Envio imediato": !findCampaign.start_immediately ?? null,
          "Data de início": findCampaign.start_date ?? null,
          "Data de término": findCampaign.expiration_date ?? null,
          "Hora de início": findCampaign.start_time !== null ? dayjs(findCampaign.start_time, "HH:mm:ss") : null,
          "Enviar SMS": findCampaign.sms_id === null ? false : true,
          "Enviar Email": findCampaign.email_id === null ? false : true,
          "Enviar WhatsApp": findCampaign.whatsapp_id === null ? false : true,
          "Canal SMS": findCampaign.sms_id === null ? false : findCampaign.sms_id,
          "Canal Email": findCampaign.email_id === null ? false : findCampaign.email_id,
          "Canal WhatsApp": findCampaign.whatsapp_id ?? null,
          "Modelo SMS": findCampaign.model_sms_id ?? null,
          "Modelo Email": findCampaign.model_email_id ?? null,
          "Modelo WhatsApp": findCampaign.model_whatsapp_id === null ? false : findCampaign.whatsapp_id,
          "Selecionar Listagem": findCampaign.list_id ?? null,
          "Em qual coluna estão as datas de vencimento?": findCampaign.key_date_row ?? null,
          "Enviar em": findCampaign.business_days ? true : false,
          "Referencia de Envio": findCampaign.reference_moment ?? null,
          "Dias": findCampaign.days_calculate ?? null,
          "Coluna da Regra": findCampaign.key_block ?? null,
          "Operador": findCampaign.when_block ?? null,
          "Valor": findCampaign.value_blocked ?? null
        }));
      })
  }

  // Save Changes
  const saveChanges = async () => {
    const lists = listOptions;
    const dataDeTermino = new Date(formState["Tipo de campanha"] === "Disparo único" ? formState["Data de início"] : formState["Data de término"]);
    const dataDeInicio = new Date(formState["Data de início"]);

    const data = {
      id_campaign: id,
      title: formState["Título da Campanha"] ? formState["Título da Campanha"] : null,
      description: formState["Descrição da Campanha"] ? formState["Descrição da Campanha"] : null,
      type: formState["Categoria"] ? formState["Categoria"] : null,
      start_immediately: formState["Envio imediato"] !== (null && "")
        ? formState["Envio imediato"] === false ? false : true
        : null,
      start_date: formState["Data de início"] ? dataDeInicio.toISOString().split("T")[0] : null,
      start_time: formState["Envio imediato"] === true
        ? dayjs().format("HH:mm")
        : formState["Hora de início"]
          ? dayjs(formState["Hora de início"]).format("HH:mm")
          : null,
      expiration_date: formState["Data de término"] ? dataDeTermino.toISOString().split("T")[0] : null,
      list_id: formState["Selecionar Listagem"] ? formState["Selecionar Listagem"] : null,
      list_type: lists.find(listagem => listagem.id === formState["Selecionar Listagem"])?.type ?? null,
      business_days: formState["Enviar em"] ? formState["Enviar em"] : false,
      dynamic_date: formState["Em qual coluna estão as datas de vencimento?"] !== "" ? true : false,
      block_dynamic: formState["Referencia de Envio"] !== "" ? true : false,
      key_date_row: formState["Em qual coluna estão as datas de vencimento?"] ? formState["Em qual coluna estão as datas de vencimento?"] : null,
      days_calculate: formState["Dias"] ? formState["Dias"] : null,
      reference_moment: formState["Referencia de Envio"] ? formState["Referencia de Envio"] : null,
      key_block: formState["Coluna da Regra"] ? formState["Coluna da Regra"] : null,
      when_block: formState["Operador"] ? formState["Operador"] : null,
      value_blocked: formState["Valor"] ? formState["Valor"] : null,
      sms_id: formState["Canal SMS"] ? formState["Canal SMS"] : null,
      whatsapp_id: formState["Canal WhatsApp"] ? formState["Canal WhatsApp"] : null,
      email_id: formState["Canal Email"] ? formState["Canal Email"] : null,
      model_sms_id: formState["Modelo SMS"] ? formState["Modelo SMS"] : null,
      model_whatsapp_id: formState["Modelo WhatsApp"] ? formState["Modelo WhatsApp"] : null,
      model_email_id: formState["Modelo Email"] ? formState["Modelo Email"] : null,
      uuid_account: userInfo["UUID"],
      visibility: true,
    };

    await CampaignUpdate(data).then((response) => {
      if (response.message === "success" || response.status === "success") {
        notify("Success", "Suas alterações foram salvas com sucesso.", true);
        setAlreadyFetchCampaigns(false);
        navigate("/campanhas");
      }
    });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", padding: "0 29px", paddingBottom: "20px" }}>
      <HeaderPage />
      <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
        <Stepper
          alternativeLabel
          activeStep={currentStep}
          connector={false}
          sx={{ maxWidth: "681px", marginTop: "31px" }}
        >
          {campaignSteps.map((step) => (
            <Step key={step.label} completed={step.number < currentStep + 1}>
              <StepLabel
                color="#000"
                sx={{
                  "& .MuiStepIcon-root": {
                    width: "41px",
                    height: "41px",
                  },
                  "& .MuiStepIcon-root.Mui-active": {
                    color: `${themeColors.purple500} !important`,
                  },
                  "& .MuiStepIcon-root.Mui-completed": {
                    color: `${themeColors.purple500} !important`,
                  },
                }}
              >
                {step.label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box sx={{ display: "flex", flexDirection: "column", gap: "20px", paddingLeft: "20px" }}>
          {renderCurrentStep(currentStep, loading)}
          <CampaignCreationFooter
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
            canAdvance={canAdvance}
            error={errorState}
            handleFinish={saveChanges}
            edit={true}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default CampaignInfo;
